import { useCurrentUser } from 'api';
import {
  ApiDataExport,
  ApiDataExportMetric,
  ApiDataExportPayloadDimension,
  ApiDataExportPostfix,
  ApiDataExportSchema,
} from 'api/apiTypes';
import { useReportGuids } from 'api/hooks/useEnvGuid';
import type { ScheduledExport } from 'api/scheduleDataExport/types';
import { withoutError } from 'api/utils';
import { Moment } from 'moment';
import React, { createContext, PropsWithChildren, useMemo, useState } from 'react';
import { ControllerState } from 'utils';

import { UiPostfix } from './components/Segmentation/segmentation.controller';
import { useParams } from './dataExport.params';

export type NewDataExportProvider = {
  uiName: ControllerState<string | null>;
  payload: ApiDataExport | null;
  metrics: ControllerState<ApiDataExportMetric[]>;
  dimensions: ControllerState<ApiDataExportPayloadDimension[]>;
  postfix: ControllerState<ApiDataExportPostfix[] | null>;
  date: {
    start: ControllerState<Moment | null>;
    end: ControllerState<Moment | null>;
  };
  format: ControllerState<'csv' | 'json'>;
  emails: ControllerState<string[]>;
  limit: ControllerState<number | null>;
  operands: ControllerState<UiPostfix[]>;
  tab: ControllerState<number>;
  /** Draft `ApiDataExport` while editing (e.g. scheduled export); cleared when leaving the editor. */
  editDraftExport: ControllerState<ApiDataExport | null>;
  /** When editing a schedule, used for UI (e.g. hide one-time export). Payload comes from `editDraftExport`. */
  editingScheduledExport: ControllerState<ScheduledExport | null>;
};

export const NewDataExportContext = createContext({} as NewDataExportProvider);

export function NewDataExportProvider(props: PropsWithChildren) {
  const { name } = useParams();
  const { envGuid } = useReportGuids();
  // const reportId = useReportId();
  // const report = withoutError(useReport(reportId));
  const user = withoutError(useCurrentUser());

  const [uiName, setUiName] = useState<string | null>(name);
  const [selectedMetrics, setSelectedMetrics] = useState<ApiDataExportMetric[]>([]);
  const [selectedDimensions, setSelectedDimensions] = useState<ApiDataExportPayloadDimension[]>([]);
  const [postfix, setPostfix] = useState<ApiDataExportPostfix[] | null>(null);
  const [startDate, setStartDate] = useState<Moment | null>(null);
  const [endDate, setEndDate] = useState<Moment | null>(null);
  const [format, setFormat] = useState<'csv' | 'json'>('csv');
  const [emails, setEmails] = useState<string[]>([]);
  const [limit, setLimit] = useState<number | null>(2000);
  const [operands, setOperands] = useState<UiPostfix[]>([]);
  const [tab, setTab] = useState<number>(0);
  const [editDraftExport, setEditDraftExport] = useState<ApiDataExport | null>(null);
  const [editingScheduledExport, setEditingScheduledExport] = useState<ScheduledExport | null>(null);

  const payload = useMemo<ApiDataExport | null>(() => {
    if (!name || !postfix || !startDate) return null;
    try {
      return ApiDataExportSchema.parse({
        name,
        emails: [user?.email, ...emails],
        format,
        filters: postfix,
        metrics: true ? [] : selectedMetrics,
        dimensions: selectedDimensions,
        environmentGuid: envGuid,
        ...(limit !== null
          ? {
              limit,
            }
          : {}),
        dateRange: {
          startDate: startDate.format('YYYY-MM-DD'),
          endDate: endDate ? endDate.format('YYYY-MM-DD') : startDate.format('YYYY-MM-DD'),
        },
      });
    } catch (e) {
      window.ApiLogs.push({
        date: new Date(),
        value: {
          path: ['data-export'],
          error: e,
        },
      });
      return null;
    }
  }, [
    emails,
    endDate,
    envGuid,
    format,
    limit,
    name,
    postfix,
    selectedDimensions,
    selectedMetrics,
    startDate,
    user?.email,
  ]);

  const value: NewDataExportProvider = useMemo(
    () => ({
      tab: { current: tab, onChange: setTab },
      uiName: { current: uiName, onChange: setUiName },
      payload,
      limit: { current: limit, onChange: setLimit },
      format: { current: format, onChange: setFormat },
      emails: { current: emails, onChange: setEmails },
      postfix: { current: postfix, onChange: setPostfix },
      operands: { current: operands, onChange: setOperands },
      metrics: { current: selectedMetrics, onChange: setSelectedMetrics },
      dimensions: { current: selectedDimensions, onChange: setSelectedDimensions },
      date: {
        start: { current: startDate, onChange: setStartDate },
        end: { current: endDate, onChange: setEndDate },
      },
      editDraftExport: { current: editDraftExport, onChange: setEditDraftExport },
      editingScheduledExport: { current: editingScheduledExport, onChange: setEditingScheduledExport },
    }),
    [
      editDraftExport,
      editingScheduledExport,
      emails,
      endDate,
      format,
      limit,
      operands,
      payload,
      postfix,
      selectedDimensions,
      selectedMetrics,
      startDate,
      tab,
      uiName,
    ]
  );

  window.ApiObjects.DataExportContext = value;

  return <NewDataExportContext.Provider value={value} {...props} />;
}
